import { shallowEqual } from '#pplx/frontend/common/shallowEqual.js';
import { threadnotificationserviceClient } from '#pplx_generated/typespec/rest/threadnotificationservice/client.js';
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
const EMPTY_MAP = new Map();
/**
 * If incoming is defined (i.e. the backend sent this field), use it
 * (coalescing null → null). Otherwise fall back to the previously
 * accumulated value, defaulting to null.
 */
function resolveField(incoming, previous) {
    if (incoming !== undefined) {
        return incoming ?? null;
    }
    return previous ?? null;
}
/**
 * Merge incoming workflow_snapshots with the previously accumulated value.
 *
 * Unlike other fields, workflow_snapshots is a dict keyed by snapshot type
 * (TODO_LIST, SHARED_FILES, USAGE_TOTAL, etc.). Each SSE batch may only
 * carry a subset of types (e.g. SHARED_FILES but not TODO_LIST) so a naive
 * replace would lose previously-received types and cause the header to
 * flicker. We shallow-merge by key so that new types overwrite stale values
 * while previously-emitted types are preserved.
 */
function mergeWorkflowSnapshots(incoming, previous) {
    if (incoming === undefined) {
        return previous ?? null;
    }
    if (incoming === null) {
        return null;
    }
    if (!previous) {
        return incoming;
    }
    return { ...previous, ...incoming };
}
// Grace period before closing the SSE connection after all subscribers disable.
// Prevents missing final notifications and avoids reconnect churn when
// threads briefly transition through states.
const DISCONNECT_GRACE_MS = 30_000;
const SSE_RETRY_BACKOFF_MS = 5_000;
const NOTIFICATION_DEDUP_TTL_MS = 5 * 60_000;
const VALID_NOTIFICATION_STATUSES = new Set([
    'ready_for_review',
    'action_needed',
    'failed',
]);
let sharedSseStream = null;
let sharedMetadata = new Map();
let sharedDisconnectTimer = null;
let sharedReconnectTimer = null;
const sharedSubscribers = new Map();
const sharedListeners = new Set();
const recentNotificationKeys = new Map();
let nextSubscriberId = 0;
function notifyListeners() {
    for (const listener of sharedListeners) {
        listener();
    }
}
function hasAnyEnabledSubscriber() {
    for (const sub of sharedSubscribers.values()) {
        if (sub.enabled)
            return true;
    }
    return false;
}
function shouldEmitNotification(data) {
    const notif = data.notification;
    if (!notif || !VALID_NOTIFICATION_STATUSES.has(notif.status)) {
        return false;
    }
    const now = Date.now();
    for (const [key, seenAt] of recentNotificationKeys) {
        if (now - seenAt > NOTIFICATION_DEDUP_TTL_MS) {
            recentNotificationKeys.delete(key);
        }
    }
    const notificationKey = [
        data.context_uuid,
        notif.status,
        notif.body,
        notif.cta_label ?? '',
        data.updated_at ?? '',
    ].join(':');
    if (recentNotificationKeys.has(notificationKey)) {
        return false;
    }
    recentNotificationKeys.set(notificationKey, now);
    return true;
}
function handleMessage(data) {
    try {
        const prev = sharedMetadata.get(data.context_uuid);
        const resolved = {
            title: resolveField(data.title, prev?.title),
            taskDescription: resolveField(data.task_description, prev?.taskDescription),
            answerPreview: resolveField(data.answer_preview, prev?.answerPreview),
            status: resolveField(data.status, prev?.status),
            unread: resolveField(data.unread, prev?.unread),
            updatedAt: resolveField(data.updated_at, prev?.updatedAt),
            lockedReason: resolveField(data.locked_reason, prev?.lockedReason),
            wakeAt: resolveField(data.wake_at, prev?.wakeAt),
            crons: resolveField(data.crons, prev?.crons),
            workflowSnapshots: mergeWorkflowSnapshots(data.workflow_snapshots, prev?.workflowSnapshots),
            subscribeEntryUuids: resolveField(data.subscribe_entry_uuids, prev?.subscribeEntryUuids),
            slug: resolveField(data.thread_url_slug, prev?.slug),
            threadAccess: resolveField(data.thread_access, prev?.threadAccess),
            sensitiveClaimsVisibility: resolveField(data.sensitive_claims_visibility, prev?.sensitiveClaimsVisibility),
        };
        if (!shallowEqual(prev, resolved)) {
            const next = new Map(sharedMetadata);
            next.set(data.context_uuid, resolved);
            sharedMetadata = next;
            notifyListeners();
        }
        // Replayed metadata can still carry events that subscribers need to handle.
        for (const sub of sharedSubscribers.values()) {
            sub.onMetadataUpdated?.(data.context_uuid, resolved);
        }
        const completedStatusChanged = data.status === 'completed' && prev?.status !== 'completed';
        const recurringRunFinished = resolved.status === 'sleeping' &&
            data.unread === true &&
            (prev?.unread !== true ||
                (data.updated_at !== undefined && data.updated_at !== prev?.updatedAt));
        if (completedStatusChanged || recurringRunFinished) {
            for (const sub of sharedSubscribers.values()) {
                sub.onStatusCompleted?.(data.context_uuid);
            }
        }
        const notif = data.notification;
        if (shouldEmitNotification(data) && notif) {
            const threadNotification = {
                status: notif.status,
                title: notif.title ?? null,
                body: notif.body,
                ctaLabel: notif.cta_label ?? null,
            };
            for (const sub of sharedSubscribers.values()) {
                sub.onNotification?.(data.context_uuid, threadNotification, resolved);
            }
        }
    }
    catch {
        // Ignore parse errors
    }
}
function ensureConnection() {
    if (sharedDisconnectTimer) {
        clearTimeout(sharedDisconnectTimer);
        sharedDisconnectTimer = null;
    }
    if (!hasAnyEnabledSubscriber())
        return;
    if (sharedSseStream || sharedReconnectTimer)
        return;
    const stream = threadnotificationserviceClient.SSE('/rest/sse/recent_thread_updates', 'recent-thread-updates', {
        method: 'GET',
        numRetries: Infinity,
        backOffTime: SSE_RETRY_BACKOFF_MS,
        handlers: {
            message: handleMessage,
        },
    });
    sharedSseStream = stream;
    // Safety guard: clear the shared reference only if it still points to
    // *this* stream. Prevents a settled stream from nulling out a newer
    // connection that was created in the meantime.
    void stream.then(() => {
        if (sharedSseStream !== stream)
            return;
        sharedSseStream = null;
        // The server periodically completes long-lived requests. Reconnect with
        // backoff while consumers remain so the shared subscription stays live
        // without spinning if the server repeatedly completes immediately.
        if (hasAnyEnabledSubscriber()) {
            sharedReconnectTimer = setTimeout(() => {
                sharedReconnectTimer = null;
                if (hasAnyEnabledSubscriber()) {
                    ensureConnection();
                }
            }, SSE_RETRY_BACKOFF_MS);
        }
    }, () => {
        if (sharedSseStream === stream)
            sharedSseStream = null;
    });
}
function maybeDisconnect() {
    if (hasAnyEnabledSubscriber())
        return;
    // No enabled subscribers — schedule graceful disconnect
    if (!sharedDisconnectTimer) {
        sharedDisconnectTimer = setTimeout(() => {
            sharedDisconnectTimer = null;
            if (sharedReconnectTimer) {
                clearTimeout(sharedReconnectTimer);
                sharedReconnectTimer = null;
            }
            if (sharedSseStream) {
                sharedSseStream.close();
                sharedSseStream = null;
            }
            if (sharedMetadata.size > 0) {
                sharedMetadata = new Map();
                notifyListeners();
            }
        }, DISCONNECT_GRACE_MS);
    }
}
function registerSubscriber(sub) {
    sharedSubscribers.set(sub.id, sub);
    if (sub.enabled) {
        ensureConnection();
    }
    return sub.id;
}
function updateSubscriber(id, enabled, onNotification, onStatusCompleted, onMetadataUpdated) {
    const sub = sharedSubscribers.get(id);
    if (!sub)
        return;
    sub.enabled = enabled;
    sub.onNotification = onNotification;
    sub.onStatusCompleted = onStatusCompleted;
    sub.onMetadataUpdated = onMetadataUpdated;
    if (enabled) {
        ensureConnection();
    }
    else {
        maybeDisconnect();
    }
}
function unregisterSubscriber(id) {
    sharedSubscribers.delete(id);
    maybeDisconnect();
}
/**
 * Programmatically update a single field in the shared thread metadata store.
 * Uses the same reactive pipeline as SSE — components re-render automatically,
 * and the next SSE message for this context_uuid will override the value.
 */
export function updateThreadMetadata(contextUuid, patch) {
    const prev = sharedMetadata.get(contextUuid);
    const resolved = {
        ...{
            title: null,
            taskDescription: null,
            answerPreview: null,
            status: null,
            unread: null,
            updatedAt: null,
            lockedReason: null,
            wakeAt: null,
            crons: null,
            workflowSnapshots: null,
            subscribeEntryUuids: null,
            slug: null,
            threadAccess: null,
            sensitiveClaimsVisibility: null,
        },
        ...prev,
        ...patch,
    };
    if (shallowEqual(prev, resolved))
        return;
    const next = new Map(sharedMetadata);
    next.set(contextUuid, resolved);
    sharedMetadata = next;
    notifyListeners();
}
/**
 * Read-only subscription to a single thread's metadata in the shared Map.
 * Unlike `useRecentThreadUpdates`, this does NOT register an SSE subscriber
 * and will NOT open an SSE connection — use it from hot-path renderers
 * (e.g. per-claim components) that only need to react to `updateThreadMetadata`
 * writes and SSE updates driven by other subscribers.
 */
export function useSharedThreadMetadata(contextUuid) {
    const subscribe = useCallback((listener) => {
        sharedListeners.add(listener);
        return () => {
            sharedListeners.delete(listener);
        };
    }, []);
    const getSnapshot = useCallback(() => (contextUuid ? sharedMetadata.get(contextUuid) : undefined), [contextUuid]);
    return useSyncExternalStore(subscribe, getSnapshot, () => undefined);
}
// ─── React hook ──────────────────────────────────────────────────────
/**
 * Subscribes to real-time thread metadata updates via SSE.
 * Keeps the shared connection open while enabled, without subscribing to renders.
 * Disabled subscribers still receive callbacks while another subscriber keeps
 * the connection open (including during the disconnect grace period).
 *
 * All hook instances share a single EventSource connection to avoid
 * exhausting the browser's per-origin HTTP/1.1 connection pool.
 *
 * When a message contains a `notification` field, the `onNotification` callback
 * fires once with the notification data and resolved metadata. The notification
 * is transient and NOT stored in the metadata map.
 */
export function useRecentThreadUpdateEvents({ enabled, onNotification, onStatusCompleted, onMetadataUpdated, }) {
    const subIdRef = useRef(null);
    const onNotificationRef = useRef(onNotification);
    onNotificationRef.current = onNotification;
    const onStatusCompletedRef = useRef(onStatusCompleted);
    onStatusCompletedRef.current = onStatusCompleted;
    const onMetadataUpdatedRef = useRef(onMetadataUpdated);
    onMetadataUpdatedRef.current = onMetadataUpdated;
    // Stable callback wrappers that delegate to the latest refs
    const stableOnNotification = useCallback((...args) => onNotificationRef.current?.(...args), []);
    const stableOnStatusCompleted = useCallback((...args) => onStatusCompletedRef.current?.(...args), []);
    const stableOnMetadataUpdated = useCallback((...args) => onMetadataUpdatedRef.current?.(...args), []);
    // Register on mount, unregister on unmount
    useEffect(() => {
        const id = nextSubscriberId++;
        subIdRef.current = id;
        registerSubscriber({
            id,
            enabled,
            onNotification: stableOnNotification,
            onStatusCompleted: stableOnStatusCompleted,
            onMetadataUpdated: stableOnMetadataUpdated,
        });
        return () => {
            unregisterSubscriber(id);
            subIdRef.current = null;
        };
        // Only run on mount/unmount — updates handled below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Sync enabled + callbacks on every render
    useEffect(() => {
        if (subIdRef.current !== null) {
            updateSubscriber(subIdRef.current, enabled, stableOnNotification, stableOnStatusCompleted, stableOnMetadataUpdated);
        }
    }, [enabled, stableOnMetadataUpdated, stableOnNotification, stableOnStatusCompleted]);
}
export function useRecentThreadMetadata(contextUuid) {
    useRecentThreadUpdateEvents({ enabled: !!contextUuid });
    return useSharedThreadMetadata(contextUuid);
}
export function useRecentThreadUpdates(options) {
    useRecentThreadUpdateEvents(options);
    const subscribe = useCallback((listener) => {
        sharedListeners.add(listener);
        return () => {
            sharedListeners.delete(listener);
        };
    }, []);
    const getSnapshot = useCallback(() => {
        if (sharedMetadata.size === 0) {
            return EMPTY_MAP;
        }
        return sharedMetadata;
    }, []);
    return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_MAP);
}
//# sourceMappingURL=useRecentThreadUpdates.js.map